import useEmblaCarousel from 'embla-carousel-react';
import { useCallback, useEffect, useState } from 'react';

type CarouselProps = {
	children: React.ReactNode;
	autoplaySpeed?: number;
	showDots?: boolean;
	className?: string;
	dotsClassName?: string;
	onSlideChange?: (index: number) => void;
	dotsOverlay?: boolean;
};

const Carousel = ({
	children,
	autoplaySpeed = 5000,
	showDots = true,
	className = 'w-full',
	dotsClassName,
	onSlideChange,
	dotsOverlay = false,
}: CarouselProps) => {
	const [emblaRef, emblaApi] = useEmblaCarousel({ loop: true });
	const [selectedIndex, setSelectedIndex] = useState(0);
	const [slideCount, setSlideCount] = useState(0);

	const onSelect = useCallback(() => {
		if (!emblaApi) return;
		const index = emblaApi.selectedScrollSnap();
		setSelectedIndex(index);
		onSlideChange?.(index);
	}, [emblaApi, onSlideChange]);

	useEffect(() => {
		if (!emblaApi) return;
		setSlideCount(emblaApi.scrollSnapList().length);
		emblaApi.on('select', onSelect);
		onSelect();
		return () => {
			emblaApi.off('select', onSelect);
		};
	}, [emblaApi, onSelect]);

	useEffect(() => {
		if (!emblaApi) return;
		const interval = setInterval(() => {
			emblaApi.scrollNext();
		}, autoplaySpeed);
		return () => clearInterval(interval);
	}, [emblaApi, autoplaySpeed]);

	return (
		<div className={dotsOverlay ? `relative ${className}` : className}>
			<div
				className='overflow-hidden'
				ref={emblaRef}
			>
				<div className='flex'>
					{Array.isArray(children)
						? children.map((child, index) => (
								<div
									key={index}
									className='flex-none w-full min-w-0'
								>
									{child}
								</div>
							))
						: <div className='flex-none w-full min-w-0'>{children}</div>}
				</div>
			</div>

			{showDots && slideCount > 1 && dotsOverlay && (
				<div className='pointer-events-none absolute inset-x-0 bottom-1.5 flex justify-center md:bottom-2'>
					<div className='pointer-events-auto flex items-center gap-1 rounded-full bg-black/25 px-1.5 py-1 backdrop-blur-sm'>
						{Array.from({ length: slideCount }).map((_, index) => (
							<button
								key={index}
								type='button'
								aria-label={`Ir al banner ${index + 1}`}
								onClick={() => emblaApi?.scrollTo(index)}
								className={`h-1.5 rounded-full transition-all duration-300 ${index === selectedIndex ? 'w-3 bg-white' : 'w-1.5 bg-white/60'}`}
							/>
						))}
					</div>
				</div>
			)}

			{showDots && slideCount > 1 && !dotsOverlay && (
				<div
					className={
						dotsClassName ?? 'flex justify-center gap-1.5 mt-2'
					}
				>
					{Array.from({ length: slideCount }).map((_, index) => (
						<button
							key={index}
							onClick={() => emblaApi?.scrollTo(index)}
							className={`h-2 rounded-full transition-all duration-300 cursor-pointer ${
								index === selectedIndex
									? 'bg-h-primary-40 w-4'
									: 'bg-h-neutral-80 w-2'
							}`}
						/>
					))}
				</div>
			)}
		</div>
	);
};

export default Carousel;
